'use client';

// components/Aria/AriaInteraction.tsx

import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAnimation } from './hooks/useAnimation';
import { useWebSpeech } from './hooks/useWebSpeech';
import { useConversation } from './hooks/useConversation';
import { useAriaAnalytics } from './hooks/useAriaAnalytics';
import { ChatMessage } from '@/types';
import { useAria } from '@/context/AriaContext';
import { AvatarCore } from './core/AvatarCore';
import styles from './ui/AvatarContainer.module.css';
import { AnimationType } from '@/types/aria';

const FORCE_RESET = true; // Set to false in production

interface AriaInteractionProps {
  config: {
    initialState: 'idle' | 'speaking' | 'listening' | 'thinking';
    avatarSize: 'small' | 'medium' | 'large';
    position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
    theme: {
      primaryColor: string;
      secondaryColor: string;
    };
    animations: {
      enabled: boolean;
      preload: string[];
      format: 'video';
      videoExtension?: string;
    };
  };
}

export const AriaInteraction: React.FC<AriaInteractionProps> = ({ config }) => {
  const { state: ariaState, dispatch } = useAria();
  const currentState = ariaState.config.animation.currentState as AnimationType;

  const { speak, startListening, stopListening, isListening, voicesReady, stop } = useWebSpeech();
  const { sendMessage } = useConversation(`${process.env.NEXT_PUBLIC_BASE_URL}/api/chat`);
  const { trackEvent } = useAriaAnalytics({
    endpoint: `${process.env.NEXT_PUBLIC_BASE_URL}/api/analytics`,
    userId: ariaState.userDetails?.name,
    debug: process.env.NODE_ENV === 'development'
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const isSpeakingRef = useRef<boolean>(false); // To track ongoing speech
  const isAnimatingRef = useRef<boolean>(false); // To track ongoing animation
  const sessionInitializedRef = useRef<boolean>(false); // To track if session is initialized

  const [isConversationVisible, setIsConversationVisible] = useState(false);
  const [isTextMode, setIsTextMode] = useState(true);
  const [isChatVisible, setIsChatVisible] = useState(false);
  const [hasSeenWelcome, setHasSeenWelcome] = useState(() => {
    if (typeof window !== 'undefined') {
      if (FORCE_RESET) {
        localStorage.removeItem('hasSeenWelcome');
        return false;
      }
      return localStorage.getItem('hasSeenWelcome') === 'true';
    }
    return false;
  });

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  useEffect(() => {
    // Initialize session if not already initialized
    const initSession = async () => {
      if (sessionInitializedRef.current) {
        console.log('Session already initialized.');
        return;
      }

      try {
        const newSessionId = uuidv4();
        const newVisitorId = uuidv4();

        // Use a relative URL to avoid CORS issues between www.eworld.global and eworld.global
        const analyticsEndpoint = '/api/analytics?endpoint=track';
        
        // Add credentials and mode to handle CORS properly
        const response = await fetch(analyticsEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-session-id': newSessionId
          },
          credentials: 'same-origin',
          mode: 'cors',
          body: JSON.stringify({
            type: 'session_init',
            event: {
              category: 'session',
              action: 'init',
              label: 'user_session'
            },
            metadata: {
              name: ariaState.userDetails?.name || 'Anonymous',
              country: ariaState.userDetails?.country || 'Unknown',
              city: ariaState.userDetails?.city || 'Unknown',
              platform: 'web',
              timestamp: new Date().toISOString()
            }
          })
        });

        if (!response.ok) {
          console.warn('Analytics initialization returned non-OK response:', response.status);
          // Don't throw an error here, just log and continue
          // This prevents analytics issues from breaking the main application
          sessionInitializedRef.current = true; // Mark as initialized anyway to prevent retries
          return;
        }

        const data = await response.json();
        // Use the newSessionId if data.sessionId is undefined
        setSessionId(data.sessionId || newSessionId);
        sessionInitializedRef.current = true;
        console.log('Session initialized with ID:', data.sessionId || newSessionId);
      } catch (error) {
        console.error('Error initializing session:', error);
        // Mark as initialized anyway to prevent continuous retries that might fail
        sessionInitializedRef.current = true;
      }
    };

    initSession();
  }, [ariaState.userDetails?.city, ariaState.userDetails?.country, ariaState.userDetails?.name]);

  const handleTextSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const inputElement = e.currentTarget.querySelector<HTMLInputElement>('.textInput');
    if (!inputElement) return;

    const text = inputElement.value.trim();
    if (!text) return;

    inputElement.value = '';
    processUserMessage(text);
  };

  const handleAgentButtonClick = async () => {
    console.log('Agent button clicked');
    setIsConversationVisible(true);

    if (!hasSeenWelcome) {
      console.log('Starting welcome sequence');
      dispatch({ type: 'SET_ANIMATION', payload: 'speaking' }); // Single dispatch
      isAnimatingRef.current = true;
      const welcomeMessage = `Hi, my name is Aria and it's a pleasure to engage with you. Welcome to E World! We're building a disruptive global marketplace platform that will transform how businesses and people connect, engage and transact worldwide. Subscribe to our waitlist now to be among the first to experience this cutting-edge platform when it is ready to launch. I'm here to help if you have any questions - you can interact with me through voice or text.`;

      try {
        console.log('Attempting to speak welcome message');

        // Ensure voices are ready before speaking
        if (!voicesReady) {
          console.warn('Voices are not ready yet. Waiting for voices to load.');
          // Wait for voices to load
          await new Promise((resolve, reject) => {
            const maxWaitTime = 5000; // 5 seconds
            const intervalTime = 100; // 100ms
            let waited = 0;

            const interval = setInterval(() => {
              if (voicesReady) {
                clearInterval(interval);
                resolve(null);
              } else {
                waited += intervalTime;
                if (waited >= maxWaitTime) {
                  clearInterval(interval);
                  reject(new Error('Voices failed to load in time.'));
                }
              }
            }, intervalTime);
          });
        }

        await speak(welcomeMessage);
        console.log('Welcome message speech completed');

        // Append Aria's welcome message to chatbox
        const ariaMessage: ChatMessage = {
          id: uuidv4(),
          role: 'assistant',
          content: welcomeMessage,
          timestamp: new Date(),
          type: 'assistant',
          format: 'text',
          metadata: {
            originalContent: welcomeMessage,
            processedContent: welcomeMessage
          }
        };
        setMessages(prev => [...prev, ariaMessage]);

        // Only log the unique welcome interaction
        // If necessary, you can log this to the database via sendMessage or another endpoint

        // Transition back to idle state after speech
        dispatch({ type: 'SET_ANIMATION', payload: 'idle' });
        isAnimatingRef.current = false;

        setHasSeenWelcome(true);
        localStorage.setItem('hasSeenWelcome', 'true');
        console.log('Welcome sequence completed');
      } catch (error) {
        console.error('Error in welcome sequence:', error);
        dispatch({ type: 'SET_ANIMATION', payload: 'idle' });
        isAnimatingRef.current = false;
      }
    } else {
      console.log('Welcome already seen, keeping current state');
    }
  };

  const handleVoiceModeClick = async (e: React.MouseEvent) => {
    console.log('1. Voice mode clicked');
    e.stopPropagation();
    setIsTextMode(false);
    setIsChatVisible(true);

    try {
      // Set to listening state and start voice input
      dispatch({ type: 'SET_ANIMATION', payload: 'listening' });
      console.log('2. Starting listening for voice input');
      const transcript = await startListening();
      console.log('3. Received transcript:', transcript);

      if (transcript) {
        await processUserMessage(transcript);
      }
    } catch (error) {
      console.error('Voice interaction error:', error);
      console.log('Error details:', error);

      if (error === 'not-allowed' || error === 'service-not-allowed') {
        alert('Microphone access was denied. Please enable microphone permissions in your browser settings to use voice features.');
      } else {
        alert(`An error occurred during voice interaction: ${error}`);
      }

      dispatch({ type: 'SET_ANIMATION', payload: 'idle' });
      isAnimatingRef.current = false;
    }
  };

  const handleTextModeClick = async () => {
    try {
      console.log('1. Text mode clicked');
      setIsTextMode(true);
      setIsChatVisible(true);
      
      // Set initial listening state
      dispatch({ type: 'SET_ANIMATION', payload: 'listening' });
      console.log('2. Set initial listening state');

      // Show text input UI
      const inputElement = document.querySelector<HTMLInputElement>('.textInput');
      if (inputElement) {
        inputElement.style.display = 'block';
        inputElement.focus();
      }
      console.log('3. Text input UI ready');
    } catch (error) {
      console.error('Error in text mode setup:', error);
      dispatch({ type: 'SET_ANIMATION', payload: 'idle' });
    }
  };

  const handleTextInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // Set to listening state when user starts typing
    if (e.target.value.length === 1) {
      dispatch({ type: 'SET_ANIMATION', payload: 'listening' });
    }
  };

  const processUserMessage = async (text: string) => {
    if (isAnimatingRef.current) {
      console.warn('Animation is already in progress. Please wait.');
      return;
    }

    enqueueInteraction(async () => {
      try {
        console.log('1. Starting interaction:', text);
        isAnimatingRef.current = true;

        // Add user message to messages state
        const userMessage: ChatMessage = {
          id: uuidv4(),
          role: 'user',
          content: text,
          timestamp: new Date(),
          type: 'user',
          format: 'text',
          metadata: {
            originalContent: text,
            processedContent: text
          }
        };
        setMessages(prev => [...prev, userMessage]);

        // Add typing indicator
        const typingMessage: ChatMessage = {
          id: uuidv4(),
          role: 'assistant',
          content: '...',
          timestamp: new Date(),
          type: 'typing',
          format: 'text'
        };
        setMessages(prev => [...prev, typingMessage]);

        // Transition to thinking animation with 3-second delay
        dispatch({ type: 'SET_ANIMATION', payload: 'thinking' });
        console.log('2. Set thinking animation');
        await new Promise(resolve => setTimeout(resolve, 3000));

        // Set a timeout to prevent getting stuck in thinking animation
        const responseTimeout = setTimeout(() => {
          console.log('Response timeout reached, resetting animation state');
          setMessages(prev => {
            const filtered = prev.filter(msg => msg.type !== 'typing');
            return filtered;
          });
          dispatch({ type: 'SET_ANIMATION', payload: 'idle' });
          isAnimatingRef.current = false;
        }, 15000); // 15 second timeout for the entire interaction

        // Send the text to get a response
        console.log('3. Sending message to get response');
        const response = await sendMessage(text);
        console.log('4. Received response:', response);
        
        // Clear the timeout since we got a response
        clearTimeout(responseTimeout);

        if (response) {
          // Remove typing indicator and add Aria's response
          setMessages(prev => {
            const filtered = prev.filter(msg => msg.type !== 'typing');
            const ariaMessage: ChatMessage = {
              id: uuidv4(),
              role: 'assistant',
              content: response,
              timestamp: new Date(),
              type: 'assistant',
              format: 'text',
              metadata: {
                originalContent: response,
                processedContent: response
              }
            };
            return [...filtered, ariaMessage];
          });

          // Transition to speaking animation
          dispatch({ type: 'SET_ANIMATION', payload: 'speaking' });
          await new Promise(resolve => setTimeout(resolve, 500));

          // Ensure voices are ready before speaking
          if (!voicesReady) {
            console.warn('Voices are not ready yet. Waiting for voices to load.');
            await new Promise((resolve, reject) => {
              const maxWaitTime = 5000;
              const intervalTime = 100;
              let waited = 0;

              const interval = setInterval(() => {
                if (voicesReady) {
                  clearInterval(interval);
                  resolve(null);
                } else {
                  waited += intervalTime;
                  if (waited >= maxWaitTime) {
                    clearInterval(interval);
                    reject(new Error('Voices failed to load in time.'));
                  }
                }
              }, intervalTime);
            });
          }

          // Speak the response regardless of input mode
          console.log('5. Starting voice response');
          await speak(response);
          console.log('6. Completed voice response');

          // Return to idle state after speaking is complete
          dispatch({ type: 'SET_ANIMATION', payload: 'idle' });
          isAnimatingRef.current = false;
          console.log('7. Set idle animation');
        } else {
          // Remove typing indicator if no response
          setMessages(prev => prev.filter(msg => msg.type !== 'typing'));
          console.log('No response received');
          dispatch({ type: 'SET_ANIMATION', payload: 'idle' });
          isAnimatingRef.current = false;
        }
      } catch (error) {
        // Cast unknown error to Error type for handleError
        handleError(error instanceof Error ? error : new Error(String(error)));
      }
    });
  };

  const handleError = (error: Error) => {
    setMessages(prev => {
      const filtered = prev.filter(msg => msg.type !== 'typing');
      const errorMessage: ChatMessage = {
        id: uuidv4(),
        role: 'assistant',  // Changed from 'system' to 'assistant' to match type
        content: "I'm sorry, I encountered an error. Please try again.",
        timestamp: new Date(),
        type: 'error',
        format: 'text'
      };
      return [...filtered, errorMessage];
    });
    console.error('Interaction error:', error);
    dispatch({ type: 'SET_ANIMATION', payload: 'idle' });
    isAnimatingRef.current = false;
  };

  // Implementing a simple queue to handle interactions sequentially
  const interactionQueueRef = useRef<(() => Promise<void>)[]>([]);

  const processQueue = async () => {
    if (interactionQueueRef.current.length === 0 || isAnimatingRef.current) return;

    isAnimatingRef.current = true;
    const nextInteraction = interactionQueueRef.current.shift();
    if (nextInteraction) {
      await nextInteraction();
    }
    isAnimatingRef.current = false;
    processQueue();
  };

  const enqueueInteraction = (interaction: () => Promise<void>) => {
    interactionQueueRef.current.push(interaction);
    processQueue();
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsConversationVisible(false);
    setIsChatVisible(false);
    dispatch({ type: 'SET_ANIMATION', payload: 'idle' });
    
    // Stop any ongoing speech and reset speech state
    stop();
    isSpeakingRef.current = false;
    isAnimatingRef.current = false;
    
    console.log('Conversation closed and speech stopped.');
  };

  const getPositionClasses = () => {
    switch (config.position) {
      case 'bottom-left':
        return 'bottom-2 left-2';
      case 'bottom-right':
        return 'bottom-2 right-2';
      case 'top-left':
        return 'top-2 left-2';
      case 'top-right':
        return 'top-2 right-2';
      default:
        return 'bottom-2 right-2';
    }
  };

  // Render Logic
  return (
    <div
      ref={containerRef}
      className={`fixed ${getPositionClasses()} z-50 flex flex-col items-end`}
      style={{
        '--primary-color': config.theme.primaryColor,
        '--secondary-color': config.theme.secondaryColor,
      } as React.CSSProperties}
      data-testid="aria-interaction"
    >
      <div className={styles.ariaContainer}>
        <div
          role="button"
          tabIndex={0}
          aria-label="AI Assistant"
          className={`${styles.mainButton} ${isConversationVisible ? styles.expandedButton : ''}`}
          onClick={handleAgentButtonClick}
          onKeyPress={(e) => e.key === 'Enter' && handleAgentButtonClick()}
        >
          <div className={styles.avatarWrapper}>
            <AvatarCore
              size={config.avatarSize}
              state={currentState}
              format={config.animations.format}
            />
            <span className={styles.label}>Aria</span>
          </div>

          {isConversationVisible && (
            <div className={styles.modeOptions}>
              <div 
                role="button"
                tabIndex={0}
                className={styles.modeOption} 
                onClick={handleVoiceModeClick}
                onKeyPress={(e: React.KeyboardEvent) => e.key === 'Enter' && handleVoiceModeClick(e as unknown as React.MouseEvent)}
              >
                Voice
              </div>
              <div 
                role="button"
                tabIndex={0}
                className={styles.modeOption} 
                onClick={handleTextModeClick}
                onKeyPress={(e: React.KeyboardEvent) => e.key === 'Enter' && handleTextModeClick()}
              >
                Text
              </div>
              <div 
                role="button"
                tabIndex={0}
                className={styles.closeButton} 
                onClick={handleClose}
                onKeyPress={(e: React.KeyboardEvent) => e.key === 'Enter' && handleClose(e as unknown as React.MouseEvent)}
              >
                Close
              </div>
            </div>
          )}
        </div>

        {isChatVisible && (
          <div className={styles.chatInterface}>
            <div className={`chatMessages ${styles.chatMessages}`}>
              {messages.map((msg) => (
                <div 
                  key={msg.id} 
                  className={`${styles.message} ${
                    msg.type === 'user' 
                      ? styles.userMessage 
                      : msg.type === 'assistant' 
                        ? styles.ariaMessage 
                        : msg.type === 'error'
                          ? styles.systemMessage
                          : styles.systemMessage
                  }`}
                >
                  {msg.content}
                </div>
              ))}
            </div>
            <form onSubmit={handleTextSubmit} className={styles.chatInput}>
              <input
                type="text"
                className="textInput"
                placeholder={isTextMode ? "Type your message..." : "Click the voice button to speak"}
                onChange={handleTextInput}
                disabled={!isTextMode}
              />
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default AriaInteraction;